<template>
  <!-- Header -->
  <header v-if="!isInitializing">
    <div class="h-header border-b border-gray-300 dark:border-warm-gray-600">
      <div class="flex w-full h-header justify-between items-center px-4">
        <h1 class="text-lg font-semibold text-gray-700 dark:text-warm-gray-50">{{ getTabTitle() }}</h1>
        <div class="flex items-center gap-4">
          <router-link to="/v2/dash" class="text-sm text-gray-700 hover:text-gray-900 border border-gray-300 rounded-md px-2.5 py-1 dark:text-warm-gray-200 dark:hover:text-white dark:border-warm-gray-500">
            <span>Legacy View</span>
          </router-link>
          <button @click="refetchData()"
            class="p-1 border rounded-full cursor-pointer flex justify-center items-center hover:bg-gray-50 border-gray-300 dark:border-warm-gray-500 dark:text-warm-gray-200 dark:hover:bg-warm-gray-800">
            <ArrowPathIcon class="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>

    <!-- Tabs -->
    <div class="flex border-b border-gray-300 bg-white dark:bg-warm-gray-800 dark:border-warm-gray-600">
      <button @click="switchTab('pending')" :class="[
        'px-6 py-3 text-sm font-medium border-b-2 transition-colors',
        activeTab === 'pending'
          ? 'border-blue-500 text-blue-600 dark:text-blue-400'
          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-warm-gray-400 dark:hover:text-warm-gray-200 dark:hover:border-warm-gray-500'
      ]">
        <span>Pending</span>
        <span v-if="pendingCount > 0"
          class="ml-2 bg-gray-200 text-gray-700 py-0.5 px-2 rounded-full text-xs font-medium dark:bg-warm-gray-700 dark:text-warm-gray-200">
          {{ pendingCount }}
        </span>
      </button>
      <button @click="switchTab('confirmed')" :class="[
        'px-6 py-3 text-sm font-medium border-b-2 transition-colors',
        activeTab === 'confirmed'
          ? 'border-blue-500 text-blue-600 dark:text-blue-400'
          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-warm-gray-400 dark:hover:text-warm-gray-200 dark:hover:border-warm-gray-500'
      ]">
        <span>Confirmed</span>
        <span v-if="confirmedCount > 0"
          class="ml-2 bg-gray-200 text-gray-700 py-0.5 px-2 rounded-full text-xs font-medium dark:bg-warm-gray-700 dark:text-warm-gray-200">
          {{ confirmedCount }}
        </span>
      </button>
      <button @click="switchTab('revoked')" :class="[
        'px-6 py-3 text-sm font-medium border-b-2 transition-colors',
        activeTab === 'revoked'
          ? 'border-blue-500 text-blue-600 dark:text-blue-400'
          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-warm-gray-400 dark:hover:text-warm-gray-200 dark:hover:border-warm-gray-500'
      ]">
        <span>Revoked</span>
        <span v-if="revokedCount > 0"
          class="ml-2 bg-gray-200 text-gray-700 py-0.5 px-2 rounded-full text-xs font-medium dark:bg-warm-gray-700 dark:text-warm-gray-200">
          {{ revokedCount }}
        </span>
      </button>
    </div>
  </header>

  <!-- Main Content -->
  <main v-if="!isInitializing" class="overflow-auto h-full p-4 scrollbar-hide">

    <div v-if="activeTab === 'pending' && displayedTransactions.length > 0" class="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 dark:bg-warm-gray-800 dark:border-warm-gray-600">
      <label class="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-warm-gray-200">
        <input
          type="checkbox"
          class="rounded border-gray-300"
          :checked="allPendingSelected"
          :disabled="selectablePending.length === 0"
          @change="toggleSelectAll"
        />
        <span>Select all ({{ selectablePending.length }})</span>
      </label>
      <div class="flex flex-wrap items-center gap-2">
        <button
          type="button"
          class="inline-flex items-center rounded-md border border-red-600 bg-white px-3 py-1.5 text-sm font-medium text-red-700 shadow-sm hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed dark:bg-warm-gray-800 dark:text-red-300 dark:border-red-700 dark:hover:bg-warm-gray-700"
          :disabled="selectedCount === 0"
          @click="revokeSelected"
        >
          Revoke selected ({{ selectedCount }})
        </button>
        <button
          type="button"
          class="inline-flex items-center rounded-md border border-green-600 bg-white px-3 py-1.5 text-sm font-medium text-green-700 shadow-sm hover:bg-green-50 disabled:opacity-40 disabled:cursor-not-allowed dark:bg-warm-gray-800 dark:text-green-300 dark:border-green-700 dark:hover:bg-warm-gray-700"
          :disabled="selectedCount === 0"
          @click="confirmSelected"
        >
          Confirm selected ({{ selectedCount }})
        </button>
      </div>
    </div>

    <!-- Transactions List -->
    <div v-if="displayedTransactions.length == 0" class="flex items-center justify-center h-64">
      <p class="text-gray-500 dark:text-warm-gray-400 text-sm">No {{ activeTab }} transactions to display</p>
    </div>

    <transition-group v-else name="dash-card" tag="ul" class="relative flex flex-col gap-4">
      <li v-for="transaction in displayedTransactions" :key="transaction.id"
        :class="[
          'dash-card-item border border-gray-300 text-xs sm:text-sm flex flex-col bg-white rounded-lg shadow-sm dark:bg-warm-gray-800 dark:border-warm-gray-600',
          busyById[transaction.id] ? 'opacity-70 pointer-events-none' : ''
        ]">

        <!-- Transaction Header -->
        <div class="px-4 pt-2 pb-2 bg-gray-50 border-b border-gray-200 rounded-t-lg dark:bg-warm-gray-900 dark:border-warm-gray-700">
          <div class="flex justify-between items-start">
            <div class="flex items-start gap-2 min-w-0">
              <input
                v-if="transaction.status === 'pending'"
                type="checkbox"
                class="mt-1.5 rounded border-gray-300"
                :checked="!!selectedIds[transaction.id]"
                :disabled="!!busyById[transaction.id]"
                @change="toggleSelect(transaction.id)"
              />
              <div>
              <p><strong class="text-base text-black dark:text-warm-gray-50">{{ transaction.user_firstname || 'N/A' }} {{ transaction.user_lastname ||
                'N/A' }}</strong></p>
              <p class="text-sm text-gray-700 -mt-1 dark:text-warm-gray-300">{{ transaction.user_email || 'N/A' }}</p>
              <p v-if="contactNumber(transaction)" class="text-sm text-gray-700 dark:text-warm-gray-300">{{ contactNumber(transaction) }}</p>
              <p v-if="schoolName(transaction)" class="text-sm text-gray-700 dark:text-warm-gray-300">{{ schoolName(transaction) }}</p>
              </div>
            </div>
            <span :class="[
              'px-3 py-1 rounded-full text-xs font-medium mt-1',
              transaction.status === 'pending' ? 'border-yellow-500 border bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:bg-opacity-40 dark:text-yellow-200' : '',
              transaction.status === 'confirmed' ? 'border-green-500 border bg-green-100 text-green-800 dark:bg-green-900 dark:bg-opacity-40 dark:text-green-200' : '',
              transaction.status === 'revoked' ? 'border-red-500 border bg-red-100 text-red-800 dark:bg-red-900 dark:bg-opacity-40 dark:text-red-200' : ''
            ]">
              {{ transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1) }}
            </span>
          </div>
        </div>

        <!-- Transaction Details -->
        <div class="px-4 py-3">
          <!-- Subscriptions -->
          <div class="pb-4">
            <div>
              <p v-if="transaction.purchase_type === 'bundled' && transaction.bundle_name"
                class="text-base text-black dark:text-warm-gray-50">
                <strong>{{ transaction.bundle_name }}</strong>
              </p>
              <p v-else-if="transaction.purchase_type === 'individual'" class="text-base text-black dark:text-warm-gray-50">
                <strong>Individual Purchase</strong>
              </p>
              
            </div>

            <!-- <p class="font-semibold text-gray-700 mb-2">Subscriptions</p> -->
            <ul class="grid grid-cols-3 gap-2 mt-2 text-gray-700 dark:text-warm-gray-200">
              <li v-for="(subscription, index) in transaction.subscriptions" :key="index"
                class="col-span-1 text-left border border-dashed border-gray-300 py-2 px-2 rounded-md dark:border-warm-gray-600">
                <p><strong>{{ subscription.reviewer_name }}</strong></p>
                <p class="text-xs text-gray-500 dark:text-warm-gray-400">{{ subscription.duration_in_words }} at ₱{{ formatPrice(subscription.original_price) }}</p>
              </li>
            </ul>
          </div>

          <div class="mb-1 text-sm">
            <p class="">
                <strong>Purchased:</strong> {{ formatDate(transaction.purchase_date) }} <span v-if="transaction.isPurchasedByAdmin && transaction.purchasedByAdminEmail" class="text-blue-500 dark:text-blue-400"> 
                <u>by admin: {{ transaction.purchasedByAdminEmail }}</u>
              </span>
              </p>
              
              <p v-if="transaction.confirmation_date" >
                <strong>Confirmed:</strong> {{ formatDate(transaction.confirmation_date) }}
                <span v-if="transaction.confirmed_by_email" class="text-blue-500 dark:text-blue-400">
                  by {{ transaction.confirmed_by_email }}
                </span>
                <span v-else-if="transaction.confirmed_by" class="text-blue-500 dark:text-blue-400">
                  by {{ transaction.confirmed_by }}
                </span>
              </p>
              <p v-if="transaction.revocation_date" class="text-red-500">
                <strong>Revoked:</strong> {{ formatDate(transaction.revocation_date) }}
                <span v-if="transaction.revoked_by_email" class="text-red-500">
                  by {{ transaction.revoked_by_email }}
                </span>
                <span v-else-if="transaction.revoked_by" class="text-red-500">
                  by {{ transaction.revoked_by }}
                </span>
              </p>
            
            <p><strong class="font-semibold">GCash Ref. No.: </strong>
              <span v-if="transaction.gcash_ref_code" class="text-gray-800 dark:text-warm-gray-200">{{ transaction.gcash_ref_code }}</span>
              <span v-else class="text-gray-400 italic dark:text-warm-gray-500">Not provided</span>
            </p>
            <div class="mt-1">
              <p class="flex flex-wrap items-baseline gap-x-1">
                <strong class="font-semibold">QR Code:</strong>
                <label
                  v-if="transaction.status === 'pending'"
                  class="qr-switcher group"
                  :class="{
                    'is-unknown text-gray-400 italic dark:text-warm-gray-500': !(transaction.qr && transaction.qr.folder),
                    'text-gray-800 dark:text-warm-gray-200': transaction.qr && transaction.qr.folder,
                    'is-disabled': isActionLocked(transaction)
                  }"
                >
                  <select
                    class="qr-switcher-select"
                    :value="transaction.qr && transaction.qr.folder ? transaction.qr.folder : ''"
                    :disabled="isActionLocked(transaction)"
                    @change="switchTransactionQr(transaction, $event.target.value)"
                  >
                    <option v-if="!transaction.qr || !transaction.qr.folder" value="">
                      Unknown
                    </option>
                    <option
                      v-for="option in qrOptionsFor(transaction)"
                      :key="option.storageFolderName"
                      :value="option.storageFolderName"
                    >
                      {{ qrLabel(option.storageFolderName, option.gcashPhoneNum) }}
                    </option>
                  </select>
                  <ChevronDownIcon class="qr-switcher-caret text-gray-400 group-hover:text-gray-600 dark:text-warm-gray-500 dark:group-hover:text-warm-gray-300" />
                </label>
                <span
                  v-else-if="transaction.qr && transaction.qr.folder"
                  class="text-gray-800 dark:text-warm-gray-200"
                >
                  {{ qrLabel(transaction.qr.folder, transaction.qr.gcash_phone) }}
                </span>
                <span v-else class="text-gray-400 italic dark:text-warm-gray-500">Unknown</span>
              </p>
              <p
                v-if="qrSwitchNote(transaction)"
                class="text-xs text-blue-500 dark:text-blue-400 mt-0.5"
              >
                {{ qrSwitchNote(transaction) }}
              </p>
            </div>
            <p><strong class="font-semibold">Payment Proof:</strong>
              <a v-if="transaction.proof_image_url" :href="transaction.proof_image_url" target="_blank"
                rel="noopener noreferrer"
                class="ml-1 inline-flex justify-start items-center text-blue-600 underline hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                <PaperClipIcon class="w-4 h-4 mr-1" />
                View Image
              </a>
              <span v-else class="text-gray-400 italic ml-1 dark:text-warm-gray-500">No image provided</span>
            </p>

            <div class="text-sm border-t border-gray-300 pt-1 mt-2 dark:border-warm-gray-600">
              <p class="font-semibold text-black dark:text-warm-gray-50">Breakdown:</p>
              <div class="text-sm">
                <div class="space-y-0">
                  <div class="flex justify-between">
                    <span>Partial Amount</span>
                    <span class="font-medium">₱{{ formatPrice(transaction.partial_amount) }}</span>
                  </div>
                  <div v-if="transaction.bundle_discount > 0" class="flex justify-between text-green-600">
                    <span>Bundle Discount</span>
                    <span class="font-medium">-₱{{ formatPrice(transaction.bundle_discount) }}</span>
                  </div>
                  <div v-if="transaction.coupon_discount > 0" class="flex justify-between text-green-600">
                    <div>
                      Coupon Discount
                      <span v-if="transaction.coupon" class="text-gray-500 text-xs border border-gray-300 rounded-md px-1 py-0.5 ml-0.5 dark:text-warm-gray-400 dark:border-warm-gray-500">{{transaction.coupon.coupon_code }}</span><span v-if="endorserDisplay(transaction)" class="font-medium ml-1 border border-blue-300 text-xs bg-blue-50 rounded-md px-1 py-0.5 text-blue-500 dark:bg-blue-900 dark:bg-opacity-30 dark:text-blue-300 dark:border-blue-700">{{ endorserDisplay(transaction) }}</span>
                    </div>
                    <span class="font-medium">-₱{{ formatPrice(transaction.coupon_discount) }}</span>
                  </div>
                </div>
                <div class="flex justify-between mt-2 pt-2 border-t border-dashed border-gray-300 font-semibold text-base dark:border-warm-gray-600">
                  <span>Total Amount</span>
                  <span>₱{{ formatPrice(transaction.total_amount) }}</span>
                </div>
              </div>
            </div>

          </div>



          <!-- Raw Data (for debugging) -->
          <!-- <div class="bg-gray-100 py-3 mt-4 text-xs text-black px-3 rounded">
            <code class="text-xs">
              {{ JSON.stringify(transaction, null, 2) }}
            </code>
          </div> -->
        </div>

        <!-- Action Buttons -->
        <div v-if="transaction.status === 'pending'"
          class="flex justify-between space-x-2 px-4 bg-gray-50 py-3 rounded-b-lg dark:bg-warm-gray-900">
          <button @click="requestRevoke(transaction)" type="button"
            class="inline-flex space-x-1 items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-xs sm:text-sm font-medium leading-4 text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed dark:bg-warm-gray-800 dark:border-warm-gray-500 dark:text-warm-gray-200 dark:hover:bg-warm-gray-700"
            :disabled="isActionLocked(transaction)">
            <HandThumbDownIcon class="w-4 h-4" />
            <span>{{ busyById[transaction.id] === 'revoking' ? 'Revoking…' : 'Revoke' }}</span>
          </button>
          <button @click="queueConfirm(transaction)" type="button"
            class="inline-flex space-x-1 items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-xs sm:text-sm font-medium leading-4 text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed dark:bg-warm-gray-800 dark:border-warm-gray-500 dark:text-warm-gray-200 dark:hover:bg-warm-gray-700"
            :disabled="isActionLocked(transaction)">
            <HandThumbUpIcon class="w-4 h-4" />
            <span>{{ busyById[transaction.id] === 'confirming' ? 'Confirming…' : 'Confirm' }}</span>
          </button>
        </div>
      </li>
    </transition-group>

    <div class="h-36"></div>
  </main>

  <!-- Loading Screen -->
  <main v-if="isInitializing" class="overflow-auto h-full w-full flex justify-center items-center">
    <Logo class="-mt-24 w-24 animate-pulse text-warm-gray-300" />
  </main>

  <!-- Notification -->
  <div v-if="noticeVisible" aria-live="assertive"
    class="absolute pointer-events-none bottom-14 inset-0 flex items-end px-2 py-4">
    <div class="flex w-full flex-col space-y-4 items-center">
      <div
        class="pointer-events-auto w-auto max-w-sm overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 dark:bg-warm-gray-800 dark:ring-warm-gray-600">
        <div class="px-6 py-3">
          <div class="flex items-center justify-between">
            <p class="flex-1 text-sm text-center font-medium text-gray-900 dark:text-warm-gray-50">{{ notificationMessage }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from "vue";
import moment from "moment";
import { onSnapshot, collection, query, where, doc, updateDoc, getDoc, getDocs, Timestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, auth, functions } from '@/firebase/config.js';
import Logo from "@/_for_dash/icons/Logo";
import {
  HandThumbUpIcon,
  HandThumbDownIcon,
  PaperClipIcon,
  ArrowPathIcon,
  ChevronDownIcon
} from "@heroicons/vue/24/outline";

const isInitializing = ref(true);
const noticeVisible = ref(false);
const notificationMessage = ref('');
const transactions = ref([]);
const activeTab = ref('pending');
const selectedIds = ref({});
const busyById = ref({});
const heldById = ref({});
let unsubscribeTransactions = null;
let actionQueue = [];
let actionQueueRunning = false;
let noticeTimer = null;
let batchConfirmOk = 0;
let batchConfirmFail = 0;
let batchRevokeOk = 0;
let batchRevokeFail = 0;
let mtpGrantFailureNotice = '';

const LEGACY_MTP_GRANT_STAGING_URL =
  'https://asia-southeast1-skilltech-mtp-staging.cloudfunctions.net/api/internal/legacy-mtp-grant';
const LEGACY_MTP_GRANT_PRODUCTION_URL =
  'https://asia-southeast1-skilltech-mtp-prod.cloudfunctions.net/api/internal/legacy-mtp-grant';

/** Same host rule as the learner app mtpAppLink: skilltech-prod → prod MTP Functions, else staging. */
function legacyMtpGrantUrl() {
  return process.env.VUE_APP_FIREBASE_PROJECT_ID === 'skilltech-prod'
    ? LEGACY_MTP_GRANT_PRODUCTION_URL
    : LEGACY_MTP_GRANT_STAGING_URL;
}

const MTP_GRANT_RESULTS = new Set(['granted', 'pending', 'already_granted']);

const schoolsById = ref({});
const endorserNameByCode = ref({});
const userExtrasById = ref({});
const inflightUserIds = new Set();
let lookupsLoaded = false;
const qrCodes = ref([]);
const switchingQrById = ref({});
const qrSwitchPromisesById = {};

const loadDashLookups = async () => {
  if (lookupsLoaded) return;
  lookupsLoaded = true;
  try {
    const [schoolsSnap, codesSnap, endorsersSnap] = await Promise.all([
      getDocs(collection(db, 'schools')),
      getDocs(collection(db, '_endorser_codes')),
      getDocs(collection(db, 'endorsers')),
    ]);

    const schools = {};
    schoolsSnap.forEach((schoolDoc) => {
      schools[schoolDoc.id] = schoolDoc.data().name || 'Unknown School';
    });
    schoolsById.value = schools;

    const namesById = {};
    endorsersSnap.forEach((endorserDoc) => {
      const data = endorserDoc.data() || {};
      const name = [data.firstName, data.lastName].filter(Boolean).join(' ').trim();
      namesById[endorserDoc.id] = name || null;
    });

    const byCode = {};
    codesSnap.forEach((codeDoc) => {
      const code = codeDoc.data()?.code;
      if (code) {
        byCode[code] = namesById[codeDoc.id] || null;
      }
    });
    endorserNameByCode.value = byCode;
  } catch (error) {
    console.error('Failed to load dash school/endorser lookups', error);
    lookupsLoaded = false;
  }
};

const contactNumber = (transaction) => {
  if (transaction.user_phone) return transaction.user_phone;
  return userExtrasById.value[transaction.user_id]?.phone || null;
};

const schoolName = (transaction) => {
  if (transaction.user_school_name) return transaction.user_school_name;
  const schoolId = transaction.user_school || userExtrasById.value[transaction.user_id]?.schoolId;
  if (!schoolId) return null;
  return schoolsById.value[schoolId] || null;
};

const endorserDisplay = (transaction) => {
  if (!transaction.coupon) return null;
  if (transaction.coupon.coupon_endorser_name) return transaction.coupon.coupon_endorser_name;
  const code = transaction.coupon.coupon_endorser_code;
  if (code && endorserNameByCode.value[code]) return endorserNameByCode.value[code];
  return code || null;
};

const qrLabel = (folder, phone) => {
  const name = folder ? String(folder).toUpperCase() : 'UNKNOWN';
  if (!phone) return name;
  const digits = String(phone);
  return `${name} (09*****${digits.slice(-4)})`;
};

const qrOptionsFor = (transaction) => {
  const list = Array.isArray(qrCodes.value) ? [...qrCodes.value] : [];
  const ensure = (folder, phone) => {
    if (!folder) return;
    if (list.some((item) => item.storageFolderName === folder)) return;
    list.push({ storageFolderName: folder, gcashPhoneNum: phone || '' });
  };
  if (transaction.qr) {
    ensure(transaction.qr.folder, transaction.qr.gcash_phone);
    ensure(transaction.qr.purchased_folder, transaction.qr.purchased_gcash_phone);
  }
  return list;
};

const qrSwitchNote = (transaction) => {
  const qr = transaction.qr;
  if (!qr || !qr.folder) return null;
  if (!qr.purchased_folder) {
    if (!qr.switched_by_email && !qr.switched_by) return null;
    return `Not stamped at purchase. Set by ${qr.switched_by_email || qr.switched_by}`;
  }
  if (qr.folder === qr.purchased_folder) return null;
  const original = qrLabel(qr.purchased_folder, qr.purchased_gcash_phone);
  const by = qr.switched_by_email || qr.switched_by;
  return by ? `Purchased as ${original}. Changed by ${by}` : `Purchased as ${original}`;
};

const fetchQrCodes = async () => {
  try {
    const snap = await getDoc(doc(db, 'settings', 'qr_codes'));
    if (snap.exists() && Array.isArray(snap.data().list)) {
      qrCodes.value = snap.data().list;
    }
  } catch (error) {
    console.error('Error fetching QR codes:', error);
  }
};

const isActionLocked = (transaction) => {
  return !!busyById.value[transaction.id] || !!switchingQrById.value[transaction.id];
};

const awaitPendingQrSwitch = async (id) => {
  const pending = qrSwitchPromisesById[id];
  if (!pending) return true;
  try {
    return await pending;
  } catch (error) {
    console.error('Error waiting for QR update:', error);
    return false;
  }
};

const switchTransactionQr = (transaction, folder) => {
  if (transaction.status !== 'pending' || !folder) return Promise.resolve(true);
  if (transaction.qr && transaction.qr.folder === folder) return Promise.resolve(true);
  if (busyById.value[transaction.id]) return Promise.resolve(false);

  const existing = qrSwitchPromisesById[transaction.id];
  if (existing) return existing;

  const selected = qrOptionsFor(transaction).find((item) => item.storageFolderName === folder);
  const phone = selected ? selected.gcashPhoneNum : '';
  const purchasedFolder = transaction.qr && transaction.qr.purchased_folder
    ? transaction.qr.purchased_folder
    : null;
  const purchasedPhone = transaction.qr && transaction.qr.purchased_gcash_phone
    ? transaction.qr.purchased_gcash_phone
    : null;
  const matchesPurchased = purchasedFolder && folder === purchasedFolder;
  const admin = auth.currentUser;
  const id = transaction.id;

  const run = (async () => {
    switchingQrById.value = { ...switchingQrById.value, [id]: true };
    try {
      const qr = {
        folder,
        gcash_phone: matchesPurchased ? (purchasedPhone || phone || null) : (phone || null),
        purchased_folder: purchasedFolder,
        purchased_gcash_phone: purchasedPhone,
        switched_by: matchesPurchased ? null : (admin ? admin.uid : null),
        switched_by_email: matchesPurchased ? null : (admin ? admin.email || null : null),
        switched_at: matchesPurchased ? null : Timestamp.now(),
      };
      await updateDoc(doc(db, 'transactions', id), { qr });
      return true;
    } catch (error) {
      console.error('Error updating transaction QR:', error);
      showNotice('Could not update QR code');
      return false;
    } finally {
      const next = { ...switchingQrById.value };
      delete next[id];
      switchingQrById.value = next;
    }
  })();

  qrSwitchPromisesById[id] = run;
  return run.finally(() => {
    if (qrSwitchPromisesById[id] === run) delete qrSwitchPromisesById[id];
  });
};

const fetchMissingUserExtras = async (list) => {
  const missing = [...new Set(
    list
      .filter((transaction) => transaction.user_id && (!transaction.user_phone || !transaction.user_school))
      .map((transaction) => transaction.user_id)
      .filter((id) => !userExtrasById.value[id] && !inflightUserIds.has(id))
  )];
  if (!missing.length) return;

  missing.forEach((id) => inflightUserIds.add(id));
  const extras = { ...userExtrasById.value };
  const chunkSize = 25;
  try {
    for (let i = 0; i < missing.length; i += chunkSize) {
      const chunk = missing.slice(i, i + chunkSize);
      await Promise.all(chunk.map(async (id) => {
        try {
          const snap = await getDoc(doc(db, 'users', id));
          if (snap.exists()) {
            const data = snap.data() || {};
            extras[id] = { phone: data.phone || null, schoolId: data.school || null };
          } else {
            extras[id] = { phone: null, schoolId: null };
          }
        } catch (error) {
          console.warn(`Failed to load user extras for ${id}`, error);
          extras[id] = { phone: null, schoolId: null };
        }
      }));
    }
    userExtrasById.value = extras;
  } finally {
    missing.forEach((id) => inflightUserIds.delete(id));
  }
};

watch(transactions, (list) => {
  fetchMissingUserExtras(list);
  const pendingIdSet = {};
  list.forEach((row) => {
    if (row.status === 'pending') pendingIdSet[row.id] = true;
  });
  const nextSelected = {};
  Object.keys(selectedIds.value).forEach((id) => {
    if (pendingIdSet[id]) nextSelected[id] = true;
  });
  selectedIds.value = nextSelected;
});

// Computed properties for counts
const pendingCount = computed(() => {
  return transactions.value.filter(t => t.status === 'pending').length;
});

const confirmedCount = computed(() => {
  return transactions.value.filter(t => t.status === 'confirmed').length;
});

const revokedCount = computed(() => {
  return transactions.value.filter(t => t.status === 'revoked').length;
});

const sortTransactionRows = (rows) => {
  return [...rows].sort((a, b) => {
    const aDate = a.purchase_date && a.purchase_date.toMillis ? a.purchase_date.toMillis() : 0;
    const bDate = b.purchase_date && b.purchase_date.toMillis ? b.purchase_date.toMillis() : 0;
    return activeTab.value === 'pending' ? aDate - bDate : bDate - aDate;
  });
};

// Filtered transactions based on active tab
const filteredTransactions = computed(() => {
  return sortTransactionRows(transactions.value.filter(t => t.status === activeTab.value));
});

const displayedTransactions = computed(() => {
  if (activeTab.value !== 'pending') return filteredTransactions.value;
  const byId = {};
  filteredTransactions.value.forEach((row) => {
    byId[row.id] = row;
  });
  Object.keys(heldById.value).forEach((id) => {
    if (!byId[id]) byId[id] = heldById.value[id];
  });
  return sortTransactionRows(Object.keys(byId).map((id) => byId[id]));
});

const selectablePending = computed(() => {
  return displayedTransactions.value.filter((row) => !busyById.value[row.id]);
});

const selectedCount = computed(() => {
  return Object.keys(selectedIds.value).length;
});

const allPendingSelected = computed(() => {
  const rows = selectablePending.value;
  if (rows.length === 0) return false;
  return rows.every((row) => selectedIds.value[row.id]);
});

const setBusy = (id, kind) => {
  busyById.value = Object.assign({}, busyById.value, { [id]: kind });
};

const clearBusy = (id) => {
  const next = Object.assign({}, busyById.value);
  delete next[id];
  busyById.value = next;
};

const holdCard = (transaction) => {
  heldById.value = Object.assign({}, heldById.value, { [transaction.id]: transaction });
};

const clearHold = (id) => {
  if (!heldById.value[id]) return;
  const next = Object.assign({}, heldById.value);
  delete next[id];
  heldById.value = next;
};

watch(transactions, (list) => {
  const pendingIdSet = {};
  list.forEach((row) => {
    if (row.status === 'pending') pendingIdSet[row.id] = true;
  });
  Object.keys(heldById.value).forEach((id) => {
    if (busyById.value[id] && !pendingIdSet[id]) {
      clearBusy(id);
      clearHold(id);
    }
  });
});

const clearSelected = (id) => {
  if (!selectedIds.value[id]) return;
  const next = Object.assign({}, selectedIds.value);
  delete next[id];
  selectedIds.value = next;
};

const toggleSelect = (id) => {
  if (busyById.value[id]) return;
  const next = Object.assign({}, selectedIds.value);
  if (next[id]) delete next[id];
  else next[id] = true;
  selectedIds.value = next;
};

const toggleSelectAll = () => {
  if (allPendingSelected.value) {
    selectedIds.value = {};
    return;
  }
  const next = {};
  selectablePending.value.forEach((row) => {
    next[row.id] = true;
  });
  selectedIds.value = next;
};

const showNotice = (message, autoHideMs) => {
  notificationMessage.value = message;
  noticeVisible.value = true;
  if (noticeTimer) {
    clearTimeout(noticeTimer);
    noticeTimer = null;
  }
  if (autoHideMs) {
    noticeTimer = setTimeout(() => {
      noticeVisible.value = false;
      noticeTimer = null;
    }, autoHideMs);
  }
};

const hideNoticeSoon = () => {
  showNotice(notificationMessage.value, 3000);
};

const formatLongDate = (date) => {
  if (!date) return 'N/A';
  // Handle Firestore Timestamp
  if (date.toMillis) {
    return moment(date.toMillis()).format('LLLL');
  }
  // Handle timestamp number
  if (typeof date === 'number') {
    return moment(date).format('LLLL');
  }
  // Handle Date object
  if (date instanceof Date) {
    return moment(date).format('LLLL');
  }
  return 'Invalid date';
};

// Formats date in the style: Sun, Nov 30, 2025 12:55AM
const formatDate = (date) => {
  if (!date) return 'N/A';
  let momentObj;
  if (date.toMillis) {
    momentObj = moment(date.toMillis());
  } else if (typeof date === 'number') {
    momentObj = moment(date);
  } else if (date instanceof Date) {
    momentObj = moment(date);
  } else {
    return 'Invalid date';
  }
  return momentObj.format('ddd, MMM DD, YYYY hh:mmA').replace(':00', '').replace(' 0', ' ');
};

const formatPrice = (price) => {
  if (!price && price !== 0) return '0.00';
  return parseFloat(price).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

/**
 * Maps reviewer_id from transaction to reviewer key used in user documents
 * @param {string} reviewerId - The reviewer_id from transaction (e.g., 'caap-knowledge')
 * @returns {string|null} - The reviewer key (e.g., 'caap') or null if not found
 */
const mapReviewerIdToKey = (reviewerId) => {
  const mapping = {
    'caap-knowledge': 'caap',
    'caap-practical': 'caap_plus',
    'entrance-exam': 'mtp'
  };
  return mapping[reviewerId] || null;
};

/**
 * Grants access to a reviewer by updating Firebase claims and Firestore document
 * @param {string} userId - The user document ID
 * @param {string} userEmail - The user's email address
 * @param {string} reviewerKey - The reviewer key ('caap', 'caap_plus', 'mtp')
 * @param {Object} subscription - The subscription data from transaction
 * @returns {Promise<boolean>} - Returns true if successful, false otherwise
 */
const grantReviewerAccess = async (userId, userEmail, reviewerKey, subscription) => {
  try {
    // Update Firebase custom claims
    let funcPayload = {};
    let keysToremove = [];

    switch (reviewerKey) {
      case 'caap':
        funcPayload = { caap: false };
        keysToremove.push('caapExpAt');
        break;
      case 'caap_plus':
        funcPayload = { caap_plus: false };
        keysToremove.push('plusExpAt');
        break;
      case 'mtp':
        funcPayload = { mtp: false };
        keysToremove.push('mtpExpAt');
        break;
      default:
        console.error(`Unknown reviewer key: ${reviewerKey}`);
        return false;
    }

    const updateUserClaimsFbFunc = httpsCallable(functions, "updateUserClaims");
    const updateUserClaimsResponse = await updateUserClaimsFbFunc({
      email: userEmail,
      payload: funcPayload,
      toRemoveKeys: keysToremove,
    });

    if (!updateUserClaimsResponse.data.success) {
      console.error(`Failed to update claims for ${reviewerKey.toUpperCase()}`);
      return false;
    }

    // Fetch user document to get existing reviewer data
    const userDocRef = doc(db, "users", userId);
    const userDocSnap = await getDoc(userDocRef);

    if (!userDocSnap.exists()) {
      console.error(`User document not found: ${userId}`);
      return false;
    }

    const userData = userDocSnap.data();

    // Update the User's Reviewer information
    let _data;
    let _reviewers;
    let _user_rev_data;

    switch (reviewerKey) {
      case 'caap':
        _user_rev_data = userData.caap_data || {};
        _user_rev_data.activateLater = true;
        _user_rev_data.activation_date = null;
        _user_rev_data.expiration_date = null;
        _data = { caap_data: _user_rev_data };
        _reviewers = {
          'reviewers.caap': 'ready'
        };
        break;
      case 'caap_plus':
        _user_rev_data = userData.caap_plus_data || {};
        _user_rev_data.activateLater = true;
        _user_rev_data.activation_date = null;
        _user_rev_data.expiration_date = null;
        _data = { caap_plus_data: _user_rev_data };
        _reviewers = {
          'reviewers.caap_plus': 'ready'
        };
        break;
      case 'mtp':
        _user_rev_data = userData.mtp_data || {};
        _user_rev_data.activateLater = true;
        _user_rev_data.activation_date = null;
        _user_rev_data.expiration_date = null;
        _data = { mtp_data: _user_rev_data };
        _reviewers = {
          'reviewers.mtp': 'ready'
        };
        break;
      default:
        console.error(`Unknown reviewer key: ${reviewerKey}`);
        return false;
    }

    // Update user document
    await updateDoc(userDocRef, { ..._reviewers, ..._data });
    console.log(`User's ${reviewerKey.toUpperCase()} access granted successfully.`);
    return true;
  } catch (error) {
    console.error(`Error granting ${reviewerKey.toUpperCase()} access:`, error);
    return false;
  }
};

/**
 * Revokes access to a reviewer by updating Firebase claims and Firestore document
 * @param {string} userId - The user document ID
 * @param {string} userEmail - The user's email address
 * @param {string} reviewerKey - The reviewer key ('caap', 'caap_plus', 'mtp')
 * @returns {Promise<boolean>} - Returns true if successful, false otherwise
 */
const revokeReviewerAccess = async (userId, userEmail, reviewerKey) => {
  try {
    // Update Firebase custom claims
    let funcPayload = {};
    let keysToremove = [];

    switch (reviewerKey) {
      case 'caap':
        funcPayload = { caap: false };
        keysToremove.push('caapExpAt');
        break;
      case 'caap_plus':
        funcPayload = { caap_plus: false };
        keysToremove.push('plusExpAt');
        break;
      case 'mtp':
        funcPayload = { mtp: false };
        keysToremove.push('mtpExpAt');
        break;
      default:
        console.error(`Unknown reviewer key: ${reviewerKey}`);
        return false;
    }

    const updateUserClaimsFbFunc = httpsCallable(functions, "updateUserClaims");
    const updateUserClaimsResponse = await updateUserClaimsFbFunc({
      email: userEmail,
      payload: funcPayload,
      toRemoveKeys: keysToremove,
    });

    if (!updateUserClaimsResponse.data.success) {
      console.error(`Failed to update claims for ${reviewerKey.toUpperCase()}`);
      return false;
    }

    // Update user document
    let _data;
    let _reviewers;

    switch (reviewerKey) {
      case 'caap':
        _data = {
          caap_data: {
            purchase_date: '',
            gcash_refno: ''
          }
        };
        _reviewers = {
          'reviewers.caap': 'inactive'
        };
        break;
      case 'caap_plus':
        _data = {
          caap_plus_data: {
            purchase_date: '',
            gcash_refno: ''
          }
        };
        _reviewers = {
          'reviewers.caap_plus': 'inactive'
        };
        break;
      case 'mtp':
        _data = {
          mtp_data: {
            purchase_date: '',
            gcash_refno: ''
          }
        };
        _reviewers = {
          'reviewers.mtp': 'inactive'
        };
        break;
      default:
        console.error(`Unknown reviewer key: ${reviewerKey}`);
        return false;
    }

    const userDocRef = doc(db, "users", userId);
    await updateDoc(userDocRef, { ..._reviewers, ..._data });
    console.log(`User's ${reviewerKey.toUpperCase()} access revoked successfully.`);
    return true;
  } catch (error) {
    console.error(`Error revoking ${reviewerKey.toUpperCase()} access:`, error);
    return false;
  }
};

const setupTransactionsListener = (status) => {
  try {
    // Query transactions by status
    const transactionsQuery = query(
      collection(db, "transactions"),
      where("status", "==", status)
    );

    // Set up real-time listener
    // onSnapshot returns the unsubscribe function synchronously
    const unsubscribe = onSnapshot(
      transactionsQuery,
      (querySnapshot) => {
        const transactionsList = [];

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          transactionsList.push({
            id: doc.id,
            ...data
          });
        });

        // Merge with existing transactions (remove old ones of this status, add new ones)
        const existingTransactions = transactions.value.filter(t => t.status !== status);
        transactions.value = [...existingTransactions, ...transactionsList];

        // Only set isInitializing to false on first load or when switching tabs
        if (isInitializing.value) {
          isInitializing.value = false;
        }
      },
      (error) => {
        console.error(`Error listening to ${status} transactions:`, error);
        notificationMessage.value = `Error listening to ${status} transactions`;
        isInitializing.value = false;
      }
    );

    return unsubscribe;
  } catch (error) {
    console.error(`Error setting up ${status} transactions listener:`, error);
    notificationMessage.value = `Error setting up ${status} transactions listener`;
    isInitializing.value = false;
    return null;
  }
};

const switchTab = (status) => {
  if (activeTab.value === status) return; // Already on this tab

  activeTab.value = status;
  selectedIds.value = {};
  isInitializing.value = true;

  // Clean up existing listener
  if (unsubscribeTransactions) {
    unsubscribeTransactions();
    unsubscribeTransactions = null;
  }

  // Only fetch if we don't have transactions for this status yet
  const hasTransactionsForStatus = transactions.value.some(t => t.status === status);
  if (!hasTransactionsForStatus) {
    unsubscribeTransactions = setupTransactionsListener(status);
    // Note: isInitializing will be set to false in the onSnapshot callback
  } else {
    // We already have data for this tab, just show it
    isInitializing.value = false;
  }
};

const getTabTitle = () => {
  const titles = {
    pending: 'Pending Transactions',
    confirmed: 'Confirmed Transactions',
    revoked: 'Revoked Transactions'
  };
  return titles[activeTab.value] || 'Transactions';
};

/**
 * Sends a transaction confirmation email to the user.
 * Uses the new transaction-based email Cloud Function which sends
 * a single email per transaction with full order details.
 */
const sendTransactionConfirmationEmail = async (transaction) => {
  try {
    // Pre-format dates and status as strings specifically for the email
    const purchaseDateForEmail = formatDate(transaction.purchase_date);
    const confirmationDateForEmail = formatDate(new Date());
    const statusForEmail = 'confirmed';

    const emailTransaction = {
      ...transaction,
      purchase_date: purchaseDateForEmail,
      confirmation_date: confirmationDateForEmail,
      status: statusForEmail,
    };

    const sendEmailFn = httpsCallable(functions, "sendEmailConfirmTransaction");
    sendEmailFn({ transactionId: transaction.id, transaction: emailTransaction }).then(() => {
      console.log(`Transaction confirmation email requested for transaction ${transaction.id}`);
    }).catch((error) => {
      console.error("Error sending transaction confirmation email:", error);
    });
  } catch (error) {
    console.error("Error sending transaction confirmation email:", error);
  }
};

const transactionLabel = (transaction) => {
  const name = [transaction.user_firstname, transaction.user_lastname].filter(Boolean).join(' ').trim();
  return name || transaction.user_email || transaction.id;
};

const queueAction = async (type, transaction) => {
  if (!auth.currentUser) {
    alert(type === 'revoke' ? "You must be logged in to revoke transactions" : "You must be logged in to confirm transactions");
    return;
  }
  if (!transaction.subscriptions || transaction.subscriptions.length === 0) {
    alert(type === 'revoke' ? "Transaction has no subscriptions to revoke" : "Transaction has no subscriptions to confirm");
    return;
  }
  if (busyById.value[transaction.id]) return;
  if (actionQueue.some((item) => item.transaction.id === transaction.id)) return;

  holdCard(transaction);
  setBusy(transaction.id, type === 'revoke' ? 'revoking' : 'confirming');

  const qrOk = await awaitPendingQrSwitch(transaction.id);
  if (!qrOk) {
    clearBusy(transaction.id);
    clearHold(transaction.id);
    const verb = type === 'revoke' ? 'Revoke' : 'Confirm';
    showNotice('Could not update QR code. ' + verb + ' cancelled.', 3000);
    return;
  }
  if (actionQueue.some((item) => item.transaction.id === transaction.id)) {
    clearBusy(transaction.id);
    clearHold(transaction.id);
    return;
  }

  actionQueue.push({ type: type, transaction: transaction });
  processActionQueue();
};

const queueConfirm = (transaction) => {
  queueAction('confirm', transaction);
};

const requestRevoke = (transaction) => {
  if (!window.confirm("Are you sure you want to revoke this transaction?")) return;
  queueAction('revoke', transaction);
};

const selectedPendingRows = () => {
  return displayedTransactions.value.filter((row) => selectedIds.value[row.id] && !busyById.value[row.id]);
};

const confirmSelected = () => {
  const rows = selectedPendingRows();
  if (rows.length === 0) return;
  if (!window.confirm('Confirm ' + rows.length + ' transaction' + (rows.length === 1 ? '' : 's') + '?')) return;
  rows.forEach((row) => queueConfirm(row));
};

const revokeSelected = () => {
  const rows = selectedPendingRows();
  if (rows.length === 0) return;
  if (!window.confirm('Revoke ' + rows.length + ' transaction' + (rows.length === 1 ? '' : 's') + '?')) return;
  rows.forEach((row) => queueAction('revoke', row));
};

const processActionQueue = async () => {
  if (actionQueueRunning) return;
  actionQueueRunning = true;
  batchConfirmOk = 0;
  batchConfirmFail = 0;
  batchRevokeOk = 0;
  batchRevokeFail = 0;
  mtpGrantFailureNotice = '';

  while (actionQueue.length) {
    const waiting = actionQueue.length - 1;
    const item = actionQueue.shift();
    const label = transactionLabel(item.transaction);
    const verb = item.type === 'revoke' ? 'Revoking' : 'Confirming';
    showNotice(waiting > 0 ? (verb + ' ' + label + '… (' + waiting + ' waiting)') : (verb + ' ' + label + '…'));
    const ok = item.type === 'revoke'
      ? await runRevoke(item.transaction)
      : await runConfirm(item.transaction);
    if (item.type === 'revoke') {
      if (ok) batchRevokeOk += 1;
      else batchRevokeFail += 1;
    } else if (ok) {
      batchConfirmOk += 1;
    } else {
      batchConfirmFail += 1;
    }
    clearSelected(item.transaction.id);
    if (ok) {
      const stillPending = transactions.value.some((row) => row.id === item.transaction.id && row.status === 'pending');
      if (!stillPending) {
        await nextTick();
        clearHold(item.transaction.id);
        clearBusy(item.transaction.id);
      }
    } else {
      clearBusy(item.transaction.id);
      clearHold(item.transaction.id);
    }
  }

  const failCount = batchConfirmFail + batchRevokeFail;
  const okCount = batchConfirmOk + batchRevokeOk;
  if (failCount === 0) {
    if (batchRevokeOk && !batchConfirmOk) {
      notificationMessage.value = batchRevokeOk === 1
        ? '✅ Transaction revoked and all reviewers access removed'
        : '✅ Revoked ' + batchRevokeOk + ' transactions';
    } else if (batchConfirmOk && !batchRevokeOk) {
      notificationMessage.value = batchConfirmOk === 1
        ? '✅ Transaction confirmed and all reviewers granted access'
        : '✅ Confirmed ' + batchConfirmOk + ' transactions';
    } else {
      notificationMessage.value = '✅ Finished ' + okCount + ' transactions';
    }
  } else if (mtpGrantFailureNotice && failCount === 1 && okCount === 0) {
    notificationMessage.value = mtpGrantFailureNotice;
  } else if (mtpGrantFailureNotice) {
    notificationMessage.value = '⚠️ Finished ' + okCount + ', failed ' + failCount + '. The new MTP app was not updated. That transaction is still pending.';
  } else {
    notificationMessage.value = '⚠️ Finished ' + okCount + ', failed ' + failCount;
  }
  mtpGrantFailureNotice = '';
  hideNoticeSoon();
  actionQueueRunning = false;
  if (actionQueue.length) processActionQueue();
};

const failMtpGrant = (message) => {
  mtpGrantFailureNotice = message;
  notificationMessage.value = message;
  return false;
};

/**
 * Marks the learner paid in the new MTP app before this transaction is confirmed.
 * Skipped when the purchase has no entrance-exam line. A failed call leaves the
 * transaction pending so Confirm can be retried.
 */
const grantMtpInNewApp = async (transaction) => {
  const subscriptions = transaction.subscriptions || [];
  const includesMtp = subscriptions.some((subscription) => subscription.reviewer_id === 'entrance-exam');
  if (!includesMtp) return true;

  if (!transaction.user_email) {
    return failMtpGrant('⚠️ The new MTP app was not updated because this transaction has no learner email. It is still pending.');
  }

  if (!auth.currentUser) {
    return failMtpGrant('⚠️ The new MTP app was not updated. This transaction is still pending.');
  }

  showNotice('Updating the new MTP app…');

  try {
    const idToken = await auth.currentUser.getIdToken();
    const response = await fetch(legacyMtpGrantUrl(), {
      method: 'POST',
      headers: {
        Authorization: 'Bearer ' + idToken,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email: transaction.user_email,
        legacyTransactionId: transaction.id,
        legacyUserId: transaction.user_id,
        purchaseType: transaction.purchase_type === 'bundled' ? 'bundle' : 'individual'
      })
    });

    if (response.status !== 200) {
      console.error('MTP grant failed with status', response.status);
      return failMtpGrant('⚠️ The new MTP app was not updated. This transaction is still pending.');
    }

    let payload = null;
    try {
      payload = await response.json();
    } catch (parseError) {
      console.error('MTP grant response was not JSON', parseError && parseError.name);
      return failMtpGrant('⚠️ The new MTP app was not updated. This transaction is still pending.');
    }

    if (!payload || !MTP_GRANT_RESULTS.has(payload.result)) {
      console.error('MTP grant response missing a success result');
      return failMtpGrant('⚠️ The new MTP app was not updated. This transaction is still pending.');
    }

    return true;
  } catch (error) {
    console.error('MTP grant request failed', error && error.name);
    return failMtpGrant('⚠️ The new MTP app was not updated. This transaction is still pending.');
  }
};

const runConfirm = async (transaction) => {
  try {
    const mtpGranted = await grantMtpInNewApp(transaction);
    if (!mtpGranted) return false;

    const results = [];
    for (let i = 0; i < transaction.subscriptions.length; i++) {
      const subscription = transaction.subscriptions[i];
      const reviewerKey = mapReviewerIdToKey(subscription.reviewer_id);

      if (!reviewerKey) {
        console.error(`Unknown reviewer_id: ${subscription.reviewer_id}`);
        results.push({ success: false, reviewer: subscription.reviewer_name, error: 'Unknown reviewer ID' });
        continue;
      }

      showNotice(`Granting access to ${subscription.reviewer_name}…`);

      const success = await grantReviewerAccess(
        transaction.user_id,
        transaction.user_email,
        reviewerKey,
        subscription
      );

      results.push({
        success,
        reviewer: subscription.reviewer_name,
        error: success ? null : 'Failed to grant access'
      });
    }

    const allSuccessful = results.every(r => r.success);
    if (!allSuccessful) {
      const failedReviewers = results.filter(r => !r.success).map(r => r.reviewer);
      console.warn(`Transaction ${transaction.id}: failed to grant access to: ${failedReviewers.join(', ')}`);
    }

    const transactionRef = doc(db, "transactions", transaction.id);
    await updateDoc(transactionRef, {
      status: "confirmed",
      confirmation_date: Timestamp.now(),
      confirmed_by: auth.currentUser.uid,
      confirmed_by_email: auth.currentUser.email || null
    });

    sendTransactionConfirmationEmail(transaction);
    return true;
  } catch (error) {
    console.error("Error confirming transaction:", error);
    return false;
  }
};

const runRevoke = async (transaction) => {
  try {
    const results = [];
    for (let i = 0; i < transaction.subscriptions.length; i++) {
      const subscription = transaction.subscriptions[i];
      const reviewerKey = mapReviewerIdToKey(subscription.reviewer_id);

      if (!reviewerKey) {
        console.error(`Unknown reviewer_id: ${subscription.reviewer_id}`);
        results.push({ success: false, reviewer: subscription.reviewer_name, error: 'Unknown reviewer ID' });
        continue;
      }

      showNotice(`Revoking access to ${subscription.reviewer_name}...`);

      const success = await revokeReviewerAccess(
        transaction.user_id,
        transaction.user_email,
        reviewerKey
      );

      results.push({
        success,
        reviewer: subscription.reviewer_name,
        error: success ? null : 'Failed to revoke access'
      });
    }

    const allSuccessful = results.every(r => r.success);
    if (!allSuccessful) {
      const failedReviewers = results.filter(r => !r.success).map(r => r.reviewer);
      console.warn(`Transaction ${transaction.id}: failed to revoke access for: ${failedReviewers.join(', ')}`);
    }

    const transactionRef = doc(db, "transactions", transaction.id);
    await updateDoc(transactionRef, {
      status: "revoked",
      revocation_date: Timestamp.now(),
      revoked_by: auth.currentUser.uid,
      revoked_by_email: auth.currentUser.email || null
    });
    return true;
  } catch (error) {
    console.error("Error revoking transaction:", error);
    return false;
  }
};

const refetchData = () => {
  // Refetch current tab's transactions
  isInitializing.value = true;
  notificationMessage.value = "Refreshing transactions...";

  // Clean up existing listener
  if (unsubscribeTransactions) {
    unsubscribeTransactions();
    unsubscribeTransactions = null;
  }

  // Remove existing transactions for current tab to force refetch
  transactions.value = transactions.value.filter(t => t.status !== activeTab.value);

  // Set up new listener (will automatically fetch and update)
  unsubscribeTransactions = setupTransactionsListener(activeTab.value);

  // Show success message after a delay
  setTimeout(() => {
    notificationMessage.value = "✅ Transactions refreshed";
    setTimeout(() => {
      notificationMessage.value = "";
    }, 2000);
  }, 500);
};

const initialize = () => {
  isInitializing.value = true;
  // Start with pending tab by default
  activeTab.value = 'pending';
  unsubscribeTransactions = setupTransactionsListener('pending');
  // Note: isInitializing will be set to false in the onSnapshot callback
};

onMounted(() => {
  loadDashLookups();
  fetchQrCodes();
  initialize();
});

onUnmounted(() => {
  // Clean up the listener when component is unmounted
  if (unsubscribeTransactions) {
    unsubscribeTransactions();
    unsubscribeTransactions = null;
  }
});
</script>

<style scoped>
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

.dash-card-move {
  transition: transform 0.3s ease;
}

.dash-card-enter-active,
.dash-card-leave-active {
  transition: opacity 0.35s ease, transform 0.35s ease;
}

.dash-card-leave-active {
  position: absolute;
  left: 0;
  right: 0;
}

.dash-card-enter-from,
.dash-card-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.qr-switcher {
  position: relative;
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  color: inherit;
}

.qr-switcher-select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background: transparent;
  border: 0;
  border-radius: 0;
  box-shadow: none;
  padding: 0 1.1rem 0 0;
  margin: 0;
  font: inherit;
  font-size: inherit;
  line-height: inherit;
  color: inherit;
  cursor: pointer;
  field-sizing: content;
  width: auto;
  min-width: 0;
  min-height: 0;
  height: auto;
  max-width: 100%;
}

.qr-switcher-select::-ms-expand {
  display: none;
}

.qr-switcher-select:focus {
  outline: none;
}

.qr-switcher-select:focus-visible {
  text-decoration: underline;
  text-underline-offset: 3px;
}

.qr-switcher-caret {
  position: absolute;
  right: 0;
  width: 0.85rem;
  height: 0.85rem;
  pointer-events: none;
}

.qr-switcher.is-disabled {
  opacity: 0.55;
  pointer-events: none;
}
</style>
